import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useIsTablet } from "@/hooks/use-tablet";
import { useSearchParams, useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useStableAuth } from "@/hooks/useStableAuth";
import { useToast } from "@/hooks/use-toast";
import { Sidebar } from "@/components/Layout/Sidebar";
import { AppHeader } from "@/components/Layout/AppHeader";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { DashboardView } from "@/components/Dashboard/DashboardView";
// ─── Lazy — Registrations (cadastros) ────────────────────────────────────────
const CustomersView = lazy(() =>
  import("@/components/Registrations/CustomersView").then((m) => ({ default: m.CustomersView }))
);
const SuppliersView = lazy(() =>
  import("@/components/Registrations/SuppliersView").then((m) => ({ default: m.SuppliersView }))
);
const ProductsView = lazy(() =>
  import("@/components/Registrations/ProductsView").then((m) => ({ default: m.ProductsView }))
);
const EmployeesView = lazy(() =>
  import("@/components/Registrations/EmployeesView").then((m) => ({ default: m.EmployeesView }))
);
const CarriersView = lazy(() =>
  import("@/components/Registrations/CarriersView").then((m) => ({ default: m.CarriersView }))
);
const DepositsView = lazy(() =>
  import("@/components/Registrations/DepositsView").then((m) => ({ default: m.DepositsView }))
);
import { RentalItemsReportView } from "@/components/Manifests/RentalItemsReportView";
import { DriverRouteView } from "@/components/Logistics/DriverRouteView";
import { StockChecklistManager } from "@/components/Stock/StockChecklistManager";
import { StockBalanceView } from "@/components/Stock/StockBalanceView"; // A8-i
import { ModuleLockedPage } from '@/components/Modules/ModuleLockedPage';
import ProductTypesPage from "@/pages/ProductTypesPage";
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { getModuleForRoute } from '@/config/modules';
import ProjectsListView from '@/components/Projects/ProjectsListView';

// ─── Lazy — Rentals & Loans ───────────────────────────────────────────────────
const RentalsView = lazy(() =>
  import("@/components/Rentals/RentalsView").then((m) => ({ default: m.RentalsView }))
);
const RentalRequestsView = lazy(() =>
  import("@/components/Rentals/RentalRequestsView").then((m) => ({ default: m.RentalRequestsView }))
);
const RentalAssetsView = lazy(() =>
  import("@/components/Rentals/RentalAssetsView").then((m) => ({ default: m.RentalAssetsView }))
);
const LoansView = lazy(() =>
  import("@/components/Loans/LoansView").then((m) => ({ default: m.LoansView }))
);
const LoansDashboard = lazy(() =>
  import("@/components/Loans/LoansDashboard").then((m) => ({ default: m.LoansDashboard }))
);

// ─── Lazy — Sales ─────────────────────────────────────────────────────────────
const UnifiedSalesView = lazy(() =>
  import("@/components/Sales/UnifiedSalesView").then((m) => ({ default: m.UnifiedSalesView }))
);
const SalesAnalyticsDashboard = lazy(() =>
  import("@/components/Sales/Dashboard/SalesAnalyticsDashboard").then((m) => ({ default: m.SalesAnalyticsDashboard }))
);
const InvoicesView = lazy(() =>
  import("@/components/Sales/InvoicesView").then((m) => ({ default: m.InvoicesView }))
);

// ─── Lazy — Logistics ─────────────────────────────────────────────────────────
const LogisticsView = lazy(() =>
  import("@/components/Logistics/LogisticsView").then((m) => ({ default: m.LogisticsView }))
);
const PickingView = lazy(() =>
  import("@/components/Logistics/PickingView").then((m) => ({ default: m.PickingView }))
);
const DeliveriesMainView = lazy(() =>
  import("@/components/Logistics/DeliveriesMainView").then((m) => ({ default: m.DeliveriesMainView }))
);
const FuelManagementView = lazy(() =>
  import("@/components/Logistics/Fuel/FuelManagementView").then((m) => ({ default: m.FuelManagementView }))
);
const TechniciansMainView = lazy(() =>
  import("@/components/Logistics/TechniciansMainView").then((m) => ({ default: m.TechniciansMainView }))
);

// ─── Lazy — Financial ─────────────────────────────────────────────────────────
const FinancialDashboardView = lazy(() =>
  import("@/components/Financial/Dashboard/FinancialDashboardView").then((m) => ({ default: m.FinancialDashboardView }))
);
const CashFlowView = lazy(() =>
  import("@/components/Financial/CashFlow/CashFlowView").then((m) => ({ default: m.CashFlowView }))
);
const ReceivablesView = lazy(() =>
  import("@/components/Financial/Receivables/ReceivablesView").then((m) => ({ default: m.ReceivablesView }))
);
const PayablesView = lazy(() =>
  import("@/components/Financial/Payables/PayablesView").then((m) => ({ default: m.PayablesView }))
);
const DREView = lazy(() =>
  import("@/components/Financial/DRE/DREView").then((m) => ({ default: m.DREView }))
);
const DREConfigurationView = lazy(() =>
  import("@/components/Financial/DRE/DREConfigurationView").then((m) => ({ default: m.DREConfigurationView }))
);
const CollectionView = lazy(() =>
  import("@/components/Financial/Collections/CollectionView").then((m) => ({ default: m.CollectionView }))
);
const BankReconciliationView = lazy(() =>
  import("@/components/Financial/Reconciliation").then((m) => ({ default: m.BankReconciliationView }))
);
const FinancialAccountsView = lazy(() =>
  import("@/components/Financial/Accounts/FinancialAccountsView").then((m) => ({ default: m.FinancialAccountsView }))
);

// ─── Lazy — CRM ───────────────────────────────────────────────────────────────
const CRMView = lazy(() =>
  import("@/components/CRM/CRMView").then((m) => ({ default: m.CRMView }))
);
const CustomerMapView = lazy(() =>
  import("@/components/CRM/CustomerMap/CustomerMapView").then((m) => ({ default: m.CustomerMapView }))
);
const AvailableCustomersPage = lazy(() =>
  import("@/components/CRM/Portfolio/AvailableCustomersPage").then((m) => ({ default: m.AvailableCustomersPage }))
);
const CylinderAnalysisView = lazy(() =>
  import("@/components/CRM/CylinderAnalysis/CylinderAnalysisView").then((m) => ({ default: m.CylinderAnalysisView }))
);

// ─── Lazy — Purchases, Services, Tasks, Reports ───────────────────────────────
const PurchasesView = lazy(() =>
  import("@/components/Purchases/PurchasesView").then((m) => ({ default: m.PurchasesView }))
);
const ServicesMainView = lazy(() =>
  import("@/components/Services/ServicesMainView").then((m) => ({ default: m.ServicesMainView }))
);
const ReportsCenterPage = lazy(() =>
  import("@/components/Reports/ReportsCenter/ReportsCenterPage").then((m) => ({ default: m.ReportsCenterPage }))
);
const DREReportView = lazy(() =>
  import("@/components/Reports/DRE/DREReportView").then((m) => ({ default: m.DREReportView }))
);
const ReportViewerPage = lazy(() =>
  import("@/components/Reports/ReportViewer/ReportViewerPage").then((m) => ({ default: m.ReportViewerPage }))
);
const ProductAnalysisPage = lazy(() =>
  import("@/pages/Reports/ProductAnalysisPage")
);
const ProductProfitabilityPage = lazy(() =>
  import("@/pages/Reports/ProductProfitabilityPage")
);
const ProductTrendReportPage = lazy(() =>
  import("@/pages/Reports/ProductTrendReportPage")
);
// REL-PAGINACAO Ato 2a: Movimentação de Estoque paginada no banco (mesma URL de antes)
const StockMovementReportPage = lazy(() =>
  import("@/pages/Reports/StockMovementReportPage")
);
// REL-PAGINACAO Ato 2b: Posição de Estoque paginada no banco (mesma URL de antes)
const StockPositionReportPage = lazy(() =>
  import("@/pages/Reports/StockPositionReportPage")
);
const SaleChangesReportPage = lazy(() =>
  import("@/pages/Reports/SaleChangesReportPage")
);
const TasksHomeView = lazy(() =>
  import("@/components/Tasks/TasksHome/TasksHomeView")
);
const MyTasksView = lazy(() =>
  import("@/components/Tasks/MyTasks/MyTasksView").then((m) => ({ default: m.MyTasksView }))
);
const NotesView = lazy(() =>
  import("@/components/Tasks/Notes/NotesView").then((m) => ({ default: m.NotesView }))
);
const InboxView = lazy(() =>
  import("@/components/Tasks/Inbox/InboxView").then((m) => ({ default: m.InboxView }))
);

// ─── Lazy — Stock & Inventory ─────────────────────────────────────────────────
const StockManagementView = lazy(() =>
  import("@/components/Stock/StockManagementView").then((m) => ({ default: m.StockManagementView }))
);
const StockEntryView = lazy(() =>
  import("@/components/Stock/StockEntryView").then((m) => ({ default: m.StockEntryView }))
);
const StockHistoryView = lazy(() =>
  import("@/components/Stock/StockHistoryView").then((m) => ({ default: m.StockHistoryView }))
);
const StockLocations = lazy(() => import("@/pages/Stock/StockLocations"));
const StockTransfers = lazy(() => import("@/pages/Stock/StockTransfers"));
const StockTransferReceivePage = lazy(() => import("@/pages/Stock/StockTransferReceivePage"));
const SefazPrOnboardingPage = lazy(() => import("@/pages/Fiscal/SefazPrOnboardingPage"));
const InventoryIntelligenceView = lazy(() =>
  import("@/components/Inventory/Intelligence/InventoryIntelligenceView").then((m) => ({ default: m.InventoryIntelligenceView }))
);

// ─── Lazy — Production (10 componentes) ──────────────────────────────────────
const ProductionProjectsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionProjectsView }))
);
const ProductionProjectDetails = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionProjectDetails }))
);
const NewProductionProjectPage = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.NewProductionProjectPage }))
);
const ProductionOperatorsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionOperatorsView }))
);
const ProductionOrdersView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionOrdersView }))
);
const ProductionItemsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionItemsView }))
);
const ProductionQCView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionQCView }))
);
const ProductionWorkLogsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionWorkLogsView }))
);
const ProductionReportsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionReportsView }))
);
const ProductionCostsView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionCostsView }))
);
const EngineeringView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.EngineeringView }))
);
const ProductionKanbanView = lazy(() =>
  import("@/components/Production").then((m) => ({ default: m.ProductionKanbanView }))
);

// ─── Lazy — Ecommerce & Settings ─────────────────────────────────────────────
const EcommerceDashboard = lazy(() =>
  import("@/components/Ecommerce/EcommerceDashboard").then((m) => ({ default: m.EcommerceDashboard }))
);
const SettingsView = lazy(() =>
  import("@/components/Settings/SettingsView").then((m) => ({ default: m.SettingsView }))
);

// ─── Suspense fallback reutilizável ──────────────────────────────────────────
const ViewLoader = () => (
  <div className="flex flex-col items-center gap-3 text-muted-foreground h-full min-h-[200px] justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    <span className="text-sm">Carregando...</span>
  </div>
);

const Index = () => {
  const isTablet = useIsTablet();
  const [activeIntent, setActiveIntent] = useState<string | null>(null);
  // UI-HEADER-FIXO ajuste (I.160): o botão de recolher o menu fica na barra; o estado mora aqui
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const toggleMenu = () => setMenuCollapsed((v) => !v);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isMaster, canAccess } = usePermissions();
  const { isPermissionsLoading, userRoles } = useStableAuth();
  const { canAccessRoute, isLoading: modulesLoading } = useModuleAccess();

  // Clear driver mode when accessing dashboard
  useEffect(() => {
    localStorage.removeItem('gonx-driver-mode');
  }, []);

  // Derivar activeView da URL e sub-rotas
  const activeView = useMemo(() => {
    const path = location.pathname.split('/').filter(Boolean);
    // /dashboard/settings → "settings"
    // /dashboard/home → "dashboard" (internamente)
    // /dashboard/reports/dre → "reports/dre"
    // /dashboard → "dashboard" (fallback)
    const view = path[1] || 'home';
    const subView = path[2];
    
    let result = view === 'home' ? 'dashboard' : view;
    if (subView) result = `${view}/${subView}`;

    return result;
  }, [location.pathname]);

  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      toast({
        title: "Pagamento Realizado com Sucesso!",
        description: "Sua assinatura foi ativada. Bem-vindo ao plano premium!",
      });
      // Remove the parameter from URL
      const newUrl = window.location.pathname;
      navigate(newUrl, { replace: true });
    } else if (canceled === 'true') {
      toast({
        title: "Pagamento Cancelado",
        description: "O processo de assinatura foi cancelado. Você pode tentar novamente a qualquer momento.",
        variant: "destructive",
      });
      // Remove the parameter from URL
      const newUrl = window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [searchParams, navigate, toast]);

  useEffect(() => {
    // Listen for navigation with intent events from the dashboard
    const handleNavigateWithIntent = (event: CustomEvent) => {
      const { section, intent } = event.detail;
      // Converter "dashboard" interno para "home" na URL
      const routeName = section === 'dashboard' ? 'home' : section;
      navigate(`/dashboard/${routeName}`);
      setActiveIntent(intent);
    };

    window.addEventListener('navigate-with-intent', handleNavigateWithIntent as EventListener);

    return () => {
      window.removeEventListener('navigate-with-intent', handleNavigateWithIntent as EventListener);
    };
  }, [navigate]);

  const handleIntentProcessed = () => {
    setActiveIntent(null); // Clear intent after it's been processed
  };

  const renderView = () => {
    // MÓDULOS: Verificar se a rota pertence a um módulo e se está ativo
    const moduleId = getModuleForRoute(activeView);
    if (moduleId && !canAccessRoute(activeView)) {
      return <ModuleLockedPage moduleId={moduleId} />;
    }

    // Interceptar rotas específicas (criação, edição e sub-rotas de compras) pela URL real
    if (
      location.pathname.includes('/editar') || 
      location.pathname.includes('/novo') ||
      location.pathname.includes('/nova/') ||
      location.pathname.includes('/importar') ||
      location.pathname.includes('/receber') ||
      location.pathname.includes('/pagar') ||
      location.pathname.includes('/purchases/cotacoes') ||
      location.pathname.includes('/purchases/invoices') ||
      location.pathname.includes('/sales/nfe/') ||
      location.pathname.match(/\/financial-accounts\/[a-f0-9-]+/) ||
      location.pathname.includes('/services/new') ||
      location.pathname.match(/\/services\/[0-9a-f-]{36}/) !== null ||
      location.pathname.match(/\/projects\/[0-9a-f-]{36}/) !== null
    ) {
      return <Outlet />;
    }

    if (/^\/dashboard\/stock-transfers\/[^/]+\/receive\/?$/.test(location.pathname)) {
      return <StockTransferReceivePage />;
    }

    // Interceptar onboarding SEFAZ-PR (rota filha vazia em App.tsx; render direto)
    if (location.pathname.includes('/fiscal/sefaz-pr')) {
      return <SefazPrOnboardingPage />;
    }

    // Interceptar criação de novo projeto de produção
    if (location.pathname === '/dashboard/production/new') {
      return <NewProductionProjectPage />;
    }
    
    // Interceptar detalhes de projeto de produção
    if (location.pathname.match(/\/production\/[a-f0-9-]+/)) {
      return <ProductionProjectDetails />;
    }
    
    switch (activeView) {
      case "dashboard":
        return <DashboardView />;
      case "customers":
        return <CustomersView intent={activeIntent} onIntentProcessed={handleIntentProcessed} />;
      case "suppliers":
        return <SuppliersView />;
      case "carriers":
        return <CarriersView />;
      case "employees":
        return <EmployeesView />;
      case "deposits":
        return <DepositsView />;
      case "products":
        return <ProductsView intent={activeIntent} onIntentProcessed={handleIntentProcessed} />;
      case "products/types":
        return <ProductTypesPage />;
      case "stock-management":
        return <StockManagementView />;
      case "stock-entry":
        return <StockEntryView />;
      case "stock-history":
        return <StockHistoryView />;
      case "stock-locations":
        return <StockLocations />;
      case "stock-transfers":
        return <StockTransfers />;
      case "stock-checklist":
        return <StockChecklistManager />;
      case "stock-balance":
        return <StockBalanceView />;
      case "inventory-intelligence":
        return <InventoryIntelligenceView />;
      case "production":
      case "production-projects":
        return <ProductionProjectsView />;
      case "production-engineering":
        return <EngineeringView />;
      case "production-kanban":
        return <ProductionKanbanView />;
      case "production-orders":
        return <ProductionOrdersView />;
      case "production-items":
        return <ProductionItemsView />;
      case "production-qc":
        return <ProductionQCView />;
      case "production-operators":
        return isMaster ? <ProductionOperatorsView /> : <Navigate to="/dashboard" replace />;
      case "production-work-logs":
        return <ProductionWorkLogsView />;
      case "production-reports":
        return <ProductionReportsView />;
      case "production-costs":
        return <ProductionCostsView />;
      case "rental-requests":
        return <RentalRequestsView />;
      case "rentals":
        return <RentalsView intent={activeIntent} onIntentProcessed={handleIntentProcessed} />;
      case "rental-assets":
        return <RentalAssetsView />;
      case "sales-dashboard":
        return (isMaster || canAccess('sales:view_dashboard'))
          ? <SalesAnalyticsDashboard />
          : <Navigate to="/dashboard" replace />;
      case "sales":
        return <UnifiedSalesView />;
      case "invoices":
        return <InvoicesView />;
      case "logistics":
        return <LogisticsView />;
      case "picking":
        return <PickingView />;
      case "logistics-deliveries":
        return <DeliveriesMainView />;
      case "logistics-fuel":
        return <FuelManagementView />;
      case "logistics-technicians":
        return isMaster ? <TechniciansMainView /> : <Navigate to="/dashboard" replace />;
      case "logistics-cylinder-analysis":
        return isMaster ? <CylinderAnalysisView /> : <Navigate to="/dashboard" replace />;
      case "loans":
        if (!canAccess('logistics:loan_view')) {
          return <Navigate to="/dashboard" replace />;
        }
        return <LoansDashboard />;
      case "financial":
        return <FinancialDashboardView />;
      case "financial-accounts":
        return <FinancialAccountsView />;
      case "dre-management":
        return (isMaster || canAccess('finance:view_dre') || canAccess('finance:manage_dre'))
          ? <DREView />
          : <Navigate to="/dashboard" replace />;
      case "dre-configuration":
        return (isMaster || canAccess('finance:manage_dre'))
          ? <DREConfigurationView />
          : <Navigate to="/dashboard" replace />;
      case "plano-contas":
        return <Outlet />;
      case "financial-cashflow":
        return <CashFlowView />;
      case "financial-receivables":
        return <ReceivablesView />;
      case "financial-payables":
        return <PayablesView />;
      case "financial-collections":
        return <CollectionView />;
      case "financial-reconciliation":
        return <BankReconciliationView />;
      // R-COMPRAS.18: rota registrada (App/modules) sem case caía no default (Dashboard) —
      // Centros de Custo vive em Configurações > Financeiro (gate da aba é o de Settings)
      case "financial-cost-centers":
        return <Navigate to="/dashboard/settings?tab=financeiro&subtab=config-financeiro&financial_tab=cost-centers" replace />;
      case "crm-interaction-report":
        return (isMaster || canAccess('crm:view_reports'))
          ? <CRMView activeTab="interaction-report" />
          : <Navigate to="/dashboard" replace />;
      case "crm":
      case "crm-portfolio":
      case "crm-routine":
      case "crm-sales-assistant":
      case "crm-create-interaction":
      case "crm-interactions":
        const crmTab = activeView.startsWith('crm-') ? activeView.replace('crm-', '') : 'portfolio';
        return <CRMView activeTab={crmTab as any} />;
      case "crm-add-customers":
        return <AvailableCustomersPage />;
      case "crm-customer-map":
        return <CustomerMapView />;
      case "crm-my-route":
        return <DriverRouteView />;
      case "purchases":
        return <PurchasesView />;
      case "services":
        return <ServicesMainView />;
      case "tasks":
        return <TasksHomeView />;
      case "tasks-my-tasks":
        return <MyTasksView />;
      case "tasks-notes":
        return <NotesView />;
      case "tasks-inbox":
        return <InboxView />;
      case "projects":
        return <ProjectsListView />;
      case "reports":
        return <ReportsCenterPage />;
      case "reports/dre":
        return <DREReportView />;
      case "reports/sale-changes":
        return <SaleChangesReportPage />;
      case "reports/product-analysis":
        return <ProductAnalysisPage />;
      case "reports/product-profitability":
        return <ProductProfitabilityPage />;
      case "reports/product-trend":
        return <ProductTrendReportPage />;
      case "reports/stock-movement":
        return <StockMovementReportPage />;
      case "reports/stock-position":
        return <StockPositionReportPage />;
      case "rental-items":
        return <RentalItemsReportView />;
      case "ecommerce":
        return <EcommerceDashboard />;
      case "settings":
        return <SettingsView />;
      default:
        // Tratar rotas dinâmicas de relatórios (reports/*)
        if (activeView.startsWith('reports/')) {
          return <ReportViewerPage />;
        }
        return <DashboardView />;
    }
  };

  // Early return só no primeiro load real (§I.98 Ato 2): oscilação transitória
  // de isPermissionsLoading com roles já carregados não desmonta as Views filhas
  // UI-HEADER-FIXO (I.160; ajuste de 23/10 tarde): fonte única de notificações em volta do painel; barra na largura
  // inteira, grudada no topo (quem rola continua sendo a janela); embaixo, lado a lado, o menu e o conteúdo
  if (isPermissionsLoading && userRoles.length === 0) {
    return (
      <NotificationsProvider>
        <div className="flex min-h-screen flex-col bg-background">
          <AppHeader collapsed={menuCollapsed} onToggleCollapsed={toggleMenu} />
          <div className="flex flex-1">
            <Sidebar collapsed={menuCollapsed} />
            <main className={`flex-1 min-w-0 overflow-auto ${isTablet ? 'p-4' : 'p-6'} flex items-center justify-center`}>
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                <span className="text-sm">Carregando permissões...</span>
              </div>
            </main>
          </div>
        </div>
      </NotificationsProvider>
    );
  }

  return (
    <NotificationsProvider>
      <div className="flex min-h-screen flex-col bg-background">
        <AppHeader collapsed={menuCollapsed} onToggleCollapsed={toggleMenu} />
        <div className="flex flex-1">
          <Sidebar collapsed={menuCollapsed} />
          <main className={`flex-1 min-w-0 overflow-auto ${isTablet ? 'p-4' : 'p-6'}`}>
            <Suspense fallback={<ViewLoader />}>
              {renderView()}
            </Suspense>
          </main>
        </div>
      </div>
    </NotificationsProvider>
  );
};

export default Index;
